import { useState } from "react";
import { Flame, Copy, Check, ImageDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { renderStreakCard, shareStreakCard } from "../lib/streakCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const COPY = {
  4: { title: "Four weeks running.", line: "A month of showing up to your own board. This is where the habit stops being a plan." },
  12: { title: "A full quarter held.", line: "Twelve weeks on the record. Most people never chair themselves once — you've done it thirteen times over." },
  26: { title: "Half a year, unbroken.", line: "Twenty-six weeks of receipts. This is a track record, not a streak." },
  52: { title: "A year in the chair.", line: "Fifty-two weeks. You've built an institution of one." },
};

/**
 * Celebration for 4/12/26/52-week streaks. Shows once per milestone — the ack
 * is stored server-side so it doesn't reappear on another device.
 */
export default function StreakMilestoneDialog({ milestone, onAcked, chairName, onTimeRate }) {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [rendering, setRendering] = useState(false);
  const copy = COPY[milestone] || COPY[4];

  const saveCard = async () => {
    setRendering(true);
    try {
      const blob = await renderStreakCard({
        weeks: milestone,
        chairName,
        onTimeRate,
        mintedLabel: new Date().toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      const how = await shareStreakCard(blob, milestone);
      if (how === "downloaded") toast.success("Card saved to your downloads.");
    } catch (e) {
      toast.error("Could not build the card.");
    } finally {
      setRendering(false);
    }
  };

  const close = async () => {
    setOpen(false);
    try {
      await api.post("/streak/milestone/ack", { weeks: milestone });
    } catch { /* the popup simply reappears next load */ }
    onAcked && onAcked(milestone);
  };

  const shareBadge = async () => {
    try {
      const r = await api.post("/streak/badge/share");
      const url = `${window.location.origin}/badge/${r.data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      window.open(url, "_blank", "noopener");
      toast.success("Badge link copied.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not mint a badge.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-[#0a0a0a] border border-[#c89f45]/30 text-[#fafafa] text-center" data-testid="streak-milestone-dialog">
        <DialogHeader>
          <DialogTitle className="sr-only">Streak milestone</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#c89f45]/60 mb-5">
            <Flame size={26} className="text-[#c89f45]" />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#c89f45]">
            {milestone}-week streak
          </p>
          <h2 className="font-serif text-3xl mt-3">{copy.title}</h2>
          <p className="text-[#bbb] mt-3 leading-relaxed max-w-sm mx-auto">{copy.line}</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 mt-8">
            <button
              type="button"
              onClick={saveCard}
              disabled={rendering}
              className="inline-flex items-center justify-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] disabled:opacity-60 text-[#0a0a0a] font-medium rounded-md px-4 py-2.5 text-sm transition-colors"
              data-testid="streak-save-card"
            >
              <ImageDown size={14} /> {rendering ? "Making the card…" : "Get the share card"}
            </button>
            <button
              type="button"
              onClick={shareBadge}
              className="inline-flex items-center justify-center gap-2 border border-[#c89f45]/50 text-[#c89f45] hover:bg-[#c89f45]/10 font-medium rounded-md px-4 py-2.5 text-sm transition-colors"
              data-testid="streak-share-badge"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} Copy badge link
            </button>
            <button
              type="button"
              onClick={close}
              className="border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md px-4 py-2.5 text-sm transition-colors"
              data-testid="streak-milestone-dismiss"
            >
              Back to work
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
